import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';

import {
    Button,
    Text,
    Flex,
    Spacer,
    TextButton,
} from '@zillow/constellation';

import AnalyticsConstants from 'app/shared/constants/AnalyticsConstants';
import analyticsUtils from 'app/client/utils/analyticsUtils';
import Constants from 'app/shared/constants';
import ModalActions from 'app/shared/actions/ModalActions';
import ModalConstants from 'app/shared/constants/ModalConstants';
import PostingActions from 'app/shared/actions/PostingActions';
import { PropertiesSelectors } from 'app/shared/selectors';
import {
    getModelId,
    getTourMode,
    updateTour,
} from 'app/client/utils/3dToursUtils';

const { GA_EVENTS, TOUR_LOCATION, TOUR_TYPE } = AnalyticsConstants;
const { track } = analyticsUtils;
const { showModal, hideModal } = ModalActions;
const { SELECT_TOUR_MODAL } = ModalConstants;
const {
    property: {
        vrModel: {
            associateListing,
            associateModel,
            getAll,
        },
    },
} = PostingActions;

const HELPER_TEXT = {
    amenity: 'property',
    floorPlan: 'floor plan',
    unit: 'unit',
    singleFamily: 'property',
};

const TOUR_HEADER_TEXT = {
    floorplan: 'Floor plan',
    unit: 'Unit',
    amenity: 'Amenity',
    singleFamily: 'Property ',
};

const LEARN_MORE_URL = 'https://www.zillow.com/marketing/3d-home/';

const TourCardEmpty = ({
    floorPlanIndex = null,
    formData,
    isDisabled,
    isHidden,
    tourType,
    unitIndex = null,
    updateFormData,
}) => {
    const dispatch = useDispatch();
    const { hasVrModels } = useSelector(state => PropertiesSelectors.makeCurrentProperty(state));
    const { aliasEncoded, isCommunity, syndication: { zillow } } = formData;

    const handleLearnMoreTourClick = () => {
        dispatch(analyticsUtils.track.GAEventV2(AnalyticsConstants.GA_EVENTS.LEARN_MORE_TOUR_CLICK, {
            label: AnalyticsConstants.TOUR_LOCATION.CARD,
        }));
    };

    const handleAssociate = ({
        shouldAssociate = false,
        tourId,
    }) => {
        if (floorPlanIndex !== null || unitIndex !== null) {
            const modelId = getModelId({
                floorPlanIndex,
                formData,
                unitIndex,
            });
            return dispatch(associateModel(tourId, aliasEncoded, modelId, shouldAssociate));
        }
        return dispatch(associateListing(tourId, aliasEncoded, shouldAssociate));
    };

    const handleAddTour = () => {
        const mode = getTourMode({
            isCommunity,
            floorPlanIndex,
            unitIndex,
        });
        const zillowDisplayStatus = zillow && zillow.displayStatus && zillow.displayStatus.toLowerCase();
        const isListingPublished = Constants.LISTING_STATUS.POSTED.toLowerCase() === zillowDisplayStatus;
        const gaEvent = GA_EVENTS.ADD_TOUR;

        dispatch(track.GAEventV2(gaEvent, { label: TOUR_LOCATION.CARD + TOUR_TYPE[mode] }));
        dispatch(showModal({
            header: `${TOUR_HEADER_TEXT[mode]} tour`,
            modalType: SELECT_TOUR_MODAL,
            initialSelectedTourId: null,
            filterOutTourIds: formData.tours.map(tour => tour.id),
            mode,
            isListingPublished,
            onCancel: () => dispatch(hideModal()),
            onSubmit: (selectedTourId = null) => {
                dispatch(track.GAEventV2(GA_EVENTS.SUBMIT_TOUR, {
                    label: TOUR_LOCATION.MODAL + TOUR_TYPE[mode],
                }));

                if (selectedTourId !== null) {
                    return handleAssociate({
                        shouldAssociate: true,
                        tourId: selectedTourId,
                    })
                        .then((tour) => {
                            updateTour({
                                floorPlanIndex,
                                formData,
                                tourToUpdate: tour,
                                tourType,
                                unitIndex,
                                updateFormData,
                            });
                            dispatch(hideModal());
                        })
                        .catch(() => Promise.reject());
                }
                dispatch(hideModal());
                return Promise.resolve();
            },
            onLoadTours: () => dispatch(getAll(aliasEncoded)),
        }));
    };

    return (
        <div className="TourCardEmpty">
            {isHidden ? (
                <div className="TourCardEmpty-text">
                    <Text type="body">
                        Floor plans with units only show 3d tours at the unit level.
                        Please add your 3d tour to individual units to have 3d tours show on our sites.
                    </Text>
                </div>
            ) : (
                <React.Fragment>
                    <Spacer paddingBottom="xs">
                        <Text as="div" fontType="bodyHeading">{`Need to showcase your ${HELPER_TEXT[tourType]}?`}</Text>
                        <Text as="div" fontType="bodySmall">Add a 3D tour using the Zillow 3D Home app.</Text>
                    </Spacer>
                    <Flex display="flex" alignItems="center">
                        {hasVrModels && (
                            <Button
                                size="sm"
                                marginRight="sm"
                                buttonType="secondary"
                                disabled={isDisabled}
                                onClick={handleAddTour}
                            >
                                Add tour
                            </Button>
                        )}
                        <Spacer paddingTop="xs" paddingBottom="xs">
                            <TextButton
                                fontType="bodySmallHeading"
                                as="a"
                                onClick={handleLearnMoreTourClick}
                                href={LEARN_MORE_URL}
                                target="_blank"
                            >
                                Learn more
                            </TextButton>
                        </Spacer>
                    </Flex>
                </React.Fragment>
            )}
        </div>
    );
};

TourCardEmpty.propTypes = {
    floorPlanIndex: PropTypes.number.isRequired,
    formData: PropTypes.object.isRequired,
    isDisabled: PropTypes.bool.isRequired,
    isHidden: PropTypes.bool,
    tourType: PropTypes.oneOf(['amenity', 'floorPlan', 'unit', 'singleFamily']).isRequired,
    unitIndex: PropTypes.number,
    updateFormData: PropTypes.func.isRequired,
};

TourCardEmpty.defaultProps = {
    isHidden: false,
    unitIndex: null,
};

export default TourCardEmpty;
